<template>
    <div>
        <b-modal
            id="changePasswordModal"
            ref="changePasswordModal"
            :lazy="true"
            title-tag="h2"
            title="Change Password"
            @hidden="resetForm"
        >
            <form class="form-horizontal">
                <div class="form-group row">
                    <label for="current" class="col-sm-5 col-form-label"
                        >Current Password:</label
                    >
                    <div class="col-sm-7">
                        <div
                            class="password-input-group w-100 position-relative"
                        >
                            <input
                                id="current"
                                v-model="data.current"
                                v-validate="'required'"
                                :type="
                                    showCurrentPassword ? 'text' : 'password'
                                "
                                :class="{
                                    'form-control': true,
                                    'border-danger':
                                        errors.has('current') ||
                                        wrongCurrentPassword
                                }"
                                name="current"
                                placeholder="Current password (required)"
                                @blur="fieldsTouched.current = true"
                                @input="wrongCurrentPassword = false"
                            />
                            <span
                                class="eye-toggle"
                                @click="
                                    showCurrentPassword = !showCurrentPassword
                                "
                            >
                                <i
                                    :class="
                                        showCurrentPassword
                                            ? 'fa fa-eye'
                                            : 'fa fa-eye-slash'
                                    "
                                ></i>
                            </span>
                        </div>
                    </div>
                </div>

                <!-- new password -->
                <div class="form-group row">
                    <label for="new" class="col-sm-5 col-form-label"
                        >New Password:</label
                    >
                    <div class="col-sm-7">
                        <div
                            class="password-input-group w-100 position-relative"
                        >
                            <input
                                id="new"
                                v-model="data.new"
                                v-validate="'required'"
                                :type="showNewPassword ? 'text' : 'password'"
                                :class="{
                                    'form-control': true,
                                    'border-danger': errors.has('new')
                                }"
                                name="new"
                                placeholder="New password (required)"
                                @blur="fieldsTouched.new = true"
                                @input="validatePasswordMatch()"
                            />
                            <span
                                class="eye-toggle"
                                @click="showNewPassword = !showNewPassword"
                            >
                                <i
                                    :class="
                                        showNewPassword
                                            ? 'fa fa-eye'
                                            : 'fa fa-eye-slash'
                                    "
                                ></i>
                            </span>
                        </div>
                    </div>
                </div>

                <!-- password strength -->
                <div class="form-group row mb-3">
                    <label class="col-sm-12 col-form-label">
                        Password Strength:
                        <strong :class="passwordStrength.color">
                            {{ passwordStrength.description }}
                        </strong>
                    </label>
                    <div class="col-sm-12">
                        <ul class="pl-0 mb-1 password-checklist">
                            <li
                                :class="{
                                    passed:
                                        passwordStrength.validations.minLength
                                }"
                            >
                                <i
                                    :class="
                                        passwordStrength.validations.minLength
                                            ? 'fa fa-check text-success'
                                            : 'fa fa-times text-danger'
                                    "
                                ></i>
                                Must be at least 12 characters
                            </li>
                            <li
                                :class="{
                                    passed:
                                        passwordStrength.validations.uppercase
                                }"
                            >
                                <i
                                    :class="
                                        passwordStrength.validations.uppercase
                                            ? 'fa fa-check text-success'
                                            : 'fa fa-times text-danger'
                                    "
                                ></i>
                                Must contain at least 1 uppercase letter
                            </li>
                            <li
                                :class="{
                                    passed:
                                        passwordStrength.validations.lowercase
                                }"
                            >
                                <i
                                    :class="
                                        passwordStrength.validations.lowercase
                                            ? 'fa fa-check text-success'
                                            : 'fa fa-times text-danger'
                                    "
                                ></i>
                                Must contain at least 1 lowercase letter
                            </li>
                            <li
                                :class="{
                                    passed: passwordStrength.validations.number
                                }"
                            >
                                <i
                                    :class="
                                        passwordStrength.validations.number
                                            ? 'fa fa-check text-success'
                                            : 'fa fa-times text-danger'
                                    "
                                ></i>
                                Must contain at least 1 number
                            </li>
                            <li
                                :class="{
                                    passed:
                                        passwordStrength.validations.specialChar
                                }"
                            >
                                <i
                                    :class="
                                        passwordStrength.validations.specialChar
                                            ? 'fa fa-check text-success'
                                            : 'fa fa-times text-danger'
                                    "
                                ></i>
                                Must contain at least 1 special character
                                (!@#$%^&*?_~-())
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- repeat password -->
                <div class="form-group row">
                    <label for="repeat" class="col-sm-5 col-form-label"
                        >Repeat New Password:</label
                    >
                    <div class="col-sm-7">
                        <div
                            class="password-input-group w-100 position-relative"
                        >
                            <input
                                id="repeat"
                                v-model="data.repeat"
                                v-validate="'required'"
                                :type="showRepeatPassword ? 'text' : 'password'"
                                :class="{
                                    'form-control': true,
                                    'border-danger':
                                        (fieldsTouched.repeat &&
                                            !data.repeat) ||
                                        (passwordMismatch &&
                                            data.new &&
                                            data.repeat)
                                }"
                                name="repeat"
                                placeholder="Repeat New password (required)"
                                @blur="fieldsTouched.repeat = true"
                                @input="validatePasswordMatch()"
                            />
                            <span
                                class="eye-toggle"
                                @click="
                                    showRepeatPassword = !showRepeatPassword
                                "
                            >
                                <i
                                    :class="
                                        showRepeatPassword
                                            ? 'fa fa-eye'
                                            : 'fa fa-eye-slash'
                                    "
                                ></i>
                            </span>
                        </div>

                        <!-- error message -->
                        <div
                            v-if="passwordMismatch && data.new && data.repeat"
                            class="text-danger small mt-1"
                        >
                            The passwords do not match
                        </div>
                    </div>
                </div>
            </form>

            <div slot="modal-footer" class="w-100">
                <b-button
                    class="float-left"
                    variant="secondary"
                    @click="$refs.changePasswordModal.hide()"
                    >Cancel</b-button
                >
                <div class="float-right">
                    <img v-if="loading" src="assets/ajax-loader.gif" />
                    <b-button
                        v-else
                        variant="primary"
                        @click="save"
                        :disabled="!formValid"
                        >Save</b-button
                    >
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import PasswordStrengthMixin from "@/mixins/password-strength";

export default {
    mixins: [PasswordStrengthMixin],
    data() {
        return {
            data: {
                current: "",
                new: "",
                repeat: ""
            },
            passwordToCheck: "",
            showCurrentPassword: false,
            showNewPassword: false,
            showRepeatPassword: false,
            loading: false,
            fieldsTouched: {
                current: false,
                new: false,
                repeat: false
            },
            wrongCurrentPassword: false,
            passwordMismatch: false
        };
    },
    computed: {
        formValid() {
            try {
                // check if password strength requirements are met:
                // 1. first verify this.passwordStrength exists (not null/undefined)
                // 2. then verify this.passwordStrength.validations exists
                // 3. then convert the validations object to an array of values
                // 4. check if EVERY validation rule returns true
                const strengthValid =
                    this.passwordStrength &&
                    this.passwordStrength.validations &&
                    Object.values(this.passwordStrength.validations).every(
                        v => v === true
                    );
                return (
                    this.data.current &&
                    this.data.new &&
                    this.data.repeat &&
                    strengthValid &&
                    !this.passwordMismatch
                );
            } catch (error) {
                console.error("Error checking form validity:", error);
                return false;
            }
        }
    },
    methods: {
        validatePasswordMatch() {
            this.passwordToCheck = this.data.new;
            const newPwd = this.data.new;
            const repeatPwd = this.data.repeat;

            // only check for mismatch if both fields have content
            if (newPwd && repeatPwd) {
                this.passwordMismatch = newPwd !== repeatPwd;
                if (
                    !this.passwordMismatch &&
                    this.errors.has("password_confirmation")
                ) {
                    this.errors.remove("password_confirmation");
                }
            } else {
                this.passwordMismatch = false;
            }
        },
        resetForm() {
            this.data = {
                current: "",
                new: "",
                repeat: ""
            };
            this.passwordMismatch = false;
            this.fieldsTouched = {
                current: false,
                new: false,
                repeat: false
            };
            this.$validator.reset();
        },
        async save() {
            this.validatePasswordMatch();
            if (this.passwordMismatch) {
                this._message(
                    "New and Repeat New passwords do not match",
                    "warning"
                );
                return;
            }

            const isValid = await this.$validator.validateAll();
            if (!isValid || !this.formValid) {
                return;
            }

            this.loading = true;

            try {
                const response = await this.$http.post(
                    process.env.ACTOR_SERVICE + "staff/change-password",
                    {
                        current: this.data.current,
                        new: this.data.new,
                        repeat: this.data.repeat
                    }
                );

                if (response.data.response.message.code === 200) {
                    const message =
                        response.data.response.resource.message ||
                        "Password changed successfully";
                    this._message(message, "success");
                    this.$refs.changePasswordModal.hide();
                } else {
                    this._message("Current password is incorrect", "warning");
                    this.wrongCurrentPassword = true;
                }
            } catch (error) {
                this._message(
                    error?.response?.data?.message ||
                        "Failed to change password",
                    "error"
                );
                this.wrongCurrentPassword = true;
            } finally {
                this.loading = false;
            }
        },
        open() {
            this.$refs.changePasswordModal.show();
        }
    }
};
</script>

<style lang="scss" scoped>
.password-input-group {
    position: relative;
}

.eye-toggle {
    cursor: pointer;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);

    .fa {
        font-size: 12px;
        color: #666666;
    }
}

.password-checklist {
    list-style: none;
    padding-left: 0;
}

.password-checklist li {
    margin-bottom: 5px;
    font-size: 14px;
}

.password-checklist li.passed {
    color: #399800;
}

.password-checklist li:not(.passed) {
    color: #ff0000;
}

#passwordStrength {
    height: 10px;
    display: block;
    margin-top: 5px;
}
</style>
